import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, XCircle, CheckCircle2, Search } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

export default function ApplicationStatus() {
  const [email, setEmail] = useState("");
  const [dot, setDot] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {found, status, first_name, applied_at, rejection_reason, reapply_path}

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/application-status", { email: email.trim(), dot_number: dot.trim() });
      setResult(data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Status check failed — please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 py-12" data-testid="status-page">
      <div className="w-full max-w-lg">
        <Link to="/" className="block text-center font-display text-2xl font-black uppercase tracking-tight mb-8">
          Boat<span className="text-[#FF3B30]">RV</span>Moving
        </Link>

        <div className="border border-white/[0.08] bg-[#0F0F10] p-8 md:p-10">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] font-bold">Driver application</span>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-2 leading-none">
            Check your status
          </h1>
          <p className="text-sm text-zinc-500 mt-3">
            Enter the email and USDOT number from your application.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="status-form" noValidate>
            <div>
              <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="you@example.com" data-testid="status-email"
                className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">USDOT number</label>
              <input inputMode="numeric" value={dot} required
                onChange={(e) => setDot(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="e.g. 3847592" data-testid="status-dot"
                className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4" />
            </div>
            <button type="submit" disabled={busy} data-testid="status-submit"
              className="w-full bg-[#FF3B30] hover:bg-[#ff5044] disabled:opacity-60 text-white font-bold text-sm uppercase tracking-widest py-4 transition-all flex items-center justify-center gap-2">
              {busy ? (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {busy ? "Checking…" : "Check status"}
            </button>
          </form>

          {result && !result.found && (
            <div className="mt-6 border border-white/[0.12] bg-white/[0.02] p-5 text-center" data-testid="status-not-found">
              <p className="text-sm text-zinc-400">{result.detail}</p>
            </div>
          )}

          {result?.found && result.status === "pending" && (
            <div className="mt-6 border border-amber-500/40 bg-amber-500/[0.05] p-5" data-testid="status-pending">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-amber-400 shrink-0" />
                <div>
                  <div className="font-display text-xl font-black uppercase text-amber-400">Under review</div>
                  <p className="text-sm text-zinc-400 mt-1">
                    {result.first_name}, your application{result.applied_at ? ` from ${new Date(result.applied_at).toLocaleDateString()}` : ""} is with
                    our review team — a real human checks every application. You'll get an email the moment there's a decision.
                  </p>
                </div>
              </div>
            </div>
          )}

          {result?.found && result.status === "rejected" && (
            <div className="mt-6 border border-red-500/40 bg-red-500/[0.05] p-5" data-testid="status-rejected">
              <div className="flex items-start gap-3">
                <XCircle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-display text-xl font-black uppercase text-red-400">Not approved</div>
                  {result.rejection_reason && (
                    <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 block mb-1">Reason</span>
                      {result.rejection_reason}
                    </p>
                  )}
                  {result.reapply_path && (
                    <Link
                      to={result.reapply_path}
                      data-testid="status-resubmit-link"
                      className="inline-block mt-4 bg-[#FF3B30] hover:bg-[#ff5044] text-white font-bold text-[11px] uppercase tracking-widest px-4 py-2.5 transition-colors"
                    >
                      Resubmit documents
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          {result?.found && result.status === "approved" && (
            <div className="mt-6 border border-emerald-500/40 bg-emerald-500/[0.05] p-5" data-testid="status-approved">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" />
                <div>
                  <div className="font-display text-xl font-black uppercase text-emerald-400">Approved</div>
                  <p className="text-sm text-zinc-400 mt-1">
                    You're in, {result.first_name}. Check your inbox for the set-password email — then{" "}
                    <Link to="/login" className="text-[#FF3B30] hover:text-white underline underline-offset-2">sign in</Link>.
                  </p>
                </div>
              </div>
            </div>
          )}

          <p className="text-[11px] text-zinc-600 text-center mt-6">
            Haven't applied yet? <Link to="/driver-signup" className="text-[#FF3B30] hover:text-white underline underline-offset-2">Register as a driver</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
